import type { DemandRecord, Product, SupplierOrder, SupplierOrderItem, SupplierProductCode } from './mock-data'
import { UNIT_SHORT } from './mock-data'
import type { ReorderRow } from './intelligence'
import type { DemandGroup } from './demand'
import { quoteNumber } from './quote'
import { esc } from './print'
import { numDateText } from './i18n'

/**
 * 1.65 — the supplier order: „შესაძენი" and the needs turned into an order per
 * supplier, sent as text or A4, received through the ordinary purchase window.
 *
 * The receipt's link to the order is its `client_ref` — `sorder:<order id>`.
 * `purchases_client_ref` (unique per shop) already makes one order one purchase:
 * the same receipt again replays, a different one is refused, and migration
 * 191's trigger closes the order in the purchase's own transaction.
 *
 * What goes to the supplier is Georgian and carries NO price: the cost is the
 * shop's, and the supplier names his own.
 */

export type DraftLine = {
  key: string
  product_id: string | null
  name: string
  unit: string | null
  qty: number
  unit_cost: number | null
  need_ids: string[]
}

export type DraftGroup = { supplier_id: string | null; supplier_name: string | null; lines: DraftLine[] }

export type LastSupplier = {
  product_id: string
  supplier_id: string
  supplier_name: string | null
  unit_cost: number
  purchased_at: string
}

/** What `save_supplier_orders` (191) takes — every id made here, so a replayed save adds nothing. */
export type SaveOrderInput = {
  id: string
  branch_id: string
  supplier_id: string | null
  supplier_name: string | null
  number: string
  note: string | null
  lines: { id: string; product_id: string | null; name: string; unit: string | null; qty: number; unit_cost: number | null; position: number }[]
  need_ids: string[]
}

const qtyText = (q: number) => String(Number(q.toFixed(3)))
const unitText = (u: string | null) => (u ? UNIT_SHORT[u as keyof typeof UNIT_SHORT] ?? u : UNIT_SHORT.piece)

/**
 * The builder's lines: one per product, whichever list it came from. A product
 * both on „შესაძენი" and asked for asks for the larger of the two — the
 * suggestion covers the sales the shop saw, the need the ones it lost.
 */
export function draftLines(reorder: ReorderRow[], needs: DemandGroup[], products: Product[]): DraftLine[] {
  const byId = new Map(products.map((p) => [p.id, p]))
  const out = new Map<string, DraftLine>()
  const cost = (id: string) => {
    const c = byId.get(id)?.purchase_price ?? 0
    return c > 0 ? c : null
  }
  for (const r of reorder) {
    out.set(`p:${r.id}`, { key: `p:${r.id}`, product_id: r.id, name: r.name, unit: r.unit, qty: r.suggest, unit_cost: cost(r.id), need_ids: [] })
  }
  for (const g of needs) {
    const key = g.product_id ? `p:${g.product_id}` : g.key
    const line = out.get(key)
    if (line) {
      line.qty = Math.max(line.qty, g.qty)
      line.need_ids.push(...g.ids)
      continue
    }
    const p = g.product_id ? byId.get(g.product_id) : undefined
    out.set(key, {
      key, product_id: g.product_id, name: p?.name ?? g.name, unit: p?.unit ?? 'piece', qty: g.qty,
      unit_cost: g.product_id ? cost(g.product_id) : null, need_ids: [...g.ids],
    })
  }
  return [...out.values()]
}

/** One group per last supplier (by name), its last cost on the line; the lines with none last. */
export function splitBySupplier(lines: DraftLine[], last: Map<string, LastSupplier>): DraftGroup[] {
  const known = new Map<string, DraftGroup>()
  const none: DraftLine[] = []
  for (const l of lines) {
    const s = l.product_id ? last.get(l.product_id) : undefined
    if (!s) { none.push(l); continue }
    const g = known.get(s.supplier_id) ?? { supplier_id: s.supplier_id, supplier_name: s.supplier_name, lines: [] }
    g.lines.push({ ...l, unit_cost: s.unit_cost > 0 ? s.unit_cost : l.unit_cost })
    known.set(s.supplier_id, g)
  }
  const groups = [...known.values()].sort((a, b) => (a.supplier_name ?? '').localeCompare(b.supplier_name ?? '', 'ka'))
  return none.length ? [...groups, { supplier_id: null, supplier_name: null, lines: none }] : groups
}

/** The quote's „ddmmyy-hhmmss" (Tbilisi); the orders of one save get -1, -2 … (Codex's design review #7). */
export function orderNumbers(now: Date, count: number): string[] {
  const n = quoteNumber(now)
  return count <= 1 ? [n] : Array.from({ length: count }, (_, i) => `${n}-${i + 1}`)
}

/** The code the supplier knows the product by: his own (by his TIN), else the barcode. */
export function orderCode(p: Product | undefined, supplierTin: string | null, codes: SupplierProductCode[]): string | null {
  if (!p) return null
  const own = supplierTin ? codes.find((c) => c.product_id === p.id && c.supplier_tin === supplierTin) : undefined
  return own?.supplier_code ?? p.barcode ?? null
}

type DocOrder = { number: string; created_at: string; note: string | null }
type DocLine = { name: string; qty: number; unit: string | null; code: string | null }
type DocShop = { name: string; phone: string | null }

/** The message a supplier gets on WhatsApp — and the same words as the A4. */
export function supplierText(o: DocOrder, lines: DocLine[], shop: DocShop): string {
  const head = [
    shop.phone ? `${shop.name} · ${shop.phone}` : shop.name,
    `შეკვეთა ${o.number} · ${numDateText(o.created_at, {}, 'ka')}`,
  ]
  const body = lines.flatMap((l, i) => [
    `${i + 1}. ${l.name} — ${qtyText(l.qty)} ${unitText(l.unit)}`,
    ...(l.code ? [`   კოდი: ${l.code}`] : []),
  ])
  const note = o.note?.trim() ? ['', `შენიშვნა: ${o.note.trim()}`] : []
  return [...head, '', ...body, ...note].join('\n')
}

/** The order on A4 — the shop, the number, the lines; no prices. Printed with `printA4`. */
export function orderA4Html(o: DocOrder, lines: DocLine[], shop: DocShop): string {
  const rows = lines.map((l, i) => `<tr><td>${i + 1}</td><td>${esc(l.name)}</td><td>${esc(l.code ?? '')}</td>`
    + `<td class="r">${esc(qtyText(l.qty))} ${esc(unitText(l.unit))}</td></tr>`).join('')
  return `<!doctype html><html lang="ka"><head><meta charset="utf-8"><title>შეკვეთა ${esc(o.number)}</title>
<style>
  @page { size: A4 portrait; margin: 14mm; }
  body { font-family: 'Noto Sans Georgian', 'Sylfaen', 'DejaVu Sans', sans-serif; color: #111; margin: 0; font-size: 12px; }
  h1 { font-size: 18px; margin: 0 0 4px; } .muted { color: #444; }
  table { width: 100%; border-collapse: collapse; margin-top: 16px; }
  th, td { border: 1px solid #999; padding: 6px 8px; text-align: left; } th { background: #eee; }
  .r { text-align: right; white-space: nowrap; }
</style></head><body>
<div><b>${esc(shop.name)}</b>${shop.phone ? ` · ${esc(shop.phone)}` : ''}</div>
<h1 style="margin-top:14px">შეკვეთა ${esc(o.number)}</h1>
<div class="muted">${esc(numDateText(o.created_at, {}, 'ka'))}</div>
<table><thead><tr><th>№</th><th>დასახელება</th><th>კოდი</th><th class="r">რაოდენობა</th></tr></thead><tbody>${rows}</tbody></table>
${o.note?.trim() ? `<p style="margin-top:14px">შენიშვნა: ${esc(o.note.trim())}</p>` : ''}
</body></html>`
}

/** What the purchase window opens with when an order's goods come. */
export function receivePreset(order: SupplierOrder, items: SupplierOrderItem[], products: Product[]): {
  supplierId: string | null
  clientRef: string
  header: string
  lines: { product_id: string; product_name: string; barcode: string | null; quantity: number; unit_cost: number }[]
  freeText: string[]
} {
  const byId = new Map(products.map((p) => [p.id, p]))
  const sorted = [...items].sort((a, b) => a.position - b.position)
  return {
    supplierId: order.supplier_id,
    clientRef: `sorder:${order.id}`,
    header: `შეკვეთა ${order.number}`,
    lines: sorted.flatMap((it) => {
      if (!it.product_id) return []
      const p = byId.get(it.product_id)
      return [{
        product_id: it.product_id, product_name: p?.name ?? it.name, barcode: p?.barcode ?? null, quantity: it.qty,
        unit_cost: it.unit_cost ?? (p && p.purchase_price > 0 ? p.purchase_price : 0),
      }]
    }),
    freeText: sorted.filter((it) => !it.product_id).map((it) => it.name),
  }
}

/** A received order: each line's ordered quantity against what the purchase took in. */
export function orderedVsReceived(
  items: SupplierOrderItem[],
  received: { product_id: string | null; product_name: string; quantity: number }[],
): { name: string; ordered: number; received: number }[] {
  const got = new Map<string, { name: string; qty: number }>()
  for (const r of received) {
    if (!r.product_id) continue
    const g = got.get(r.product_id) ?? { name: r.product_name, qty: 0 }
    g.qty += r.quantity
    got.set(r.product_id, g)
  }
  const rows = [...items].sort((a, b) => a.position - b.position).map((it) => {
    const g = it.product_id ? got.get(it.product_id) : undefined
    if (it.product_id) got.delete(it.product_id)
    return { name: it.name, ordered: it.qty, received: g?.qty ?? 0 }
  })
  return [...rows, ...[...got.values()].map((g) => ({ name: g.name, ordered: 0, received: g.qty }))]
}

/** „შეკვეთილია 8 · 26.10" on a reorder row: what the branch's OPEN orders hold per product. */
export function openOrderMarks(orders: SupplierOrder[], items: SupplierOrderItem[], branchId: string | null): Map<string, { qty: number; at: string }> {
  const open = new Map(orders.filter((o) => o.status === 'open' && (!branchId || o.branch_id === branchId)).map((o) => [o.id, o]))
  const out = new Map<string, { qty: number; at: string }>()
  for (const it of items) {
    const o = open.get(it.order_id)
    if (!o || !it.product_id) continue
    const m = out.get(it.product_id) ?? { qty: 0, at: o.created_at }
    m.qty += it.qty
    if (o.created_at > m.at) m.at = o.created_at
    out.set(it.product_id, m)
  }
  return out
}

/**
 * Why „მიღება" is not offered: the order is no longer open, or — in a shop with
 * branches — another branch is active. The purchase, the drawer shift, the paid
 * source and the shelf copy all follow the active branch (Codex's design review #1).
 */
export function receiveBlock(order: SupplierOrder, activeBranchId: string | null, multiBranch: boolean): 'branch' | 'status' | null {
  if (order.status !== 'open') return 'status'
  if (multiBranch && activeBranchId !== order.branch_id) return 'branch'
  return null
}

/** Rows as PostgREST sends them (numeric may arrive as a string). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapSupplierOrder(r: any): SupplierOrder {
  return {
    id: r.id, branch_id: r.branch_id, supplier_id: r.supplier_id ?? null, supplier_name: r.supplier_name ?? null,
    number: r.number, status: r.status, note: r.note ?? null, created_at: r.created_at,
    sent_at: r.sent_at ?? null, closed_at: r.closed_at ?? null, purchase_id: r.purchase_id ?? null,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapSupplierOrderItem(r: any): SupplierOrderItem {
  return {
    id: r.id, order_id: r.order_id, product_id: r.product_id ?? null, name: r.name, unit: r.unit ?? null,
    qty: Number(r.qty), unit_cost: r.unit_cost == null ? null : Number(r.unit_cost), position: r.position ?? 0,
  }
}

/**
 * The needs an order keeps after an edit: those it holds whose line is still on it — by product, or for the
 * buyer's words by the trimmed name (Codex's final review #3: read from a FRESH copy of the needs at save time,
 * since `edit_supplier_order` releases every held need that is not sent back).
 */
export function heldNeedIds(demands: DemandRecord[], orderId: string, lines: DraftLine[]): string[] {
  return demands.filter((d) => d.order_id === orderId && lines.some((l) => (
    d.product_id ? l.product_id === d.product_id : !l.product_id && l.name.trim() === d.name.trim()
  ))).map((d) => d.id)
}
